import * as React from 'react';
import { createElement } from 'react';
import * as ReactMarkdown from 'react-markdown';
import { InternalTranslation as Tr } from '../Global/Translation';
import { TranslationInterface } from '../Interfaces/TranslationsInterface';
import axios from 'axios'
import './Translation.scss';
import { getAppConfig } from '../../../config';

export function reactMD(
  key: string,
  opts?: {
    inline?: boolean;
    replacements?: { [key: string]: any };
  }
) {
  let result = Translation.l(key);
  opts = opts || {};
  if (opts.replacements) {
    for (const key in opts.replacements) {
      result = result.replace(new RegExp(key), opts.replacements[key] || 'ig');
    }
  }
  const classes = ['__react-md-wrapper'];
  if (opts.inline) {
    classes.push('__react-md-wrapper--inline');
  }
  return (
    <span className={classes.join(' ')}>
      <ReactMarkdown linkTarget="_blank" source={result} />
    </span>
  );
}

export const generateDangerHTMLFromText = (text) => ({__html: text});

export const generateDangerHTMLFromKey = (key) => generateDangerHTMLFromText(l(key));

export class Translation {
  private static loaded: boolean = false;
  private static translations: TranslationInterface = {};

  public static get hasLoaded() {
    return this.loaded;
  }

  public static get all(): TranslationInterface {
    return this.translations;
  }

  public static l(key: string, prefix?: string) {
    if (!this.loaded) {
      return key;
    } else {
      if (this.translations[key]) {
        return Translation.replaceBrand(this.translations[key]);
      } else {
        if (prefix) {
          return Translation.replaceBrand(this.translations[this.getKeyByPrefix(key, prefix)]);
        }
        return `[${key}]`;
      }
    }
  }

  public static replaceBrand(text = '') {
    text = text.replace('</br>', '\n').replace('<br>', '\n');
    return text.replace(/\[BRAND\]/g, function (a, b) {
      return (window as any).initialConfiguration.brand || '';
    });
  }

  public static md(key: string) {
    return <ReactMarkdown source={this.translations[key] || key} />;
  }

  public static tsxl(key: string, prefix?: string): JSX.Element {
    if (!prefix) {
      return createElement(Tr, {
        translationKey: key
      });
    } else {
      return createElement(Tr, {
        translationKey: this.getKeyByPrefix(key, prefix)
      });
    }
  }

  private static getKeyByPrefix(key: string, prefix: string) {
    let pfKey, defaultPfKey;
    pfKey = prefix + '.' + key;
    defaultPfKey = 'invoice.' + key;
    if (this.translations[pfKey]) {
      return pfKey;
    } else if (this.translations[defaultPfKey]) {
      return defaultPfKey;
    } else {
      return defaultPfKey;
    }
  }

  public static async load(language: string) {
    if (this.loaded) {
      return this.translations;
    }
    const {env} = getAppConfig();
    const domain = env === 'prod' ? 'translations.zervant.com' : 'test-translations.zerv.me';
    const url = `https://${domain}/lang_${language}.json`;
    const {data} = await axios.get(url);
    Translation.payload = data;
    return data;
  }

  public static set payload(translations: TranslationInterface) {
    this.translations = translations;
    this.loaded = true;
  }

  /*
  public static dev() {
    let fi = {};
    let en = {};

    try {
      GET<TranslationsInterface>('/core/translations/lang_en_GB.json').then((data) => {
        en = data;
      });

      GET<TranslationsInterface>('/core/translations/lang_fi_FI.json').then((data) => {
        fi = data;
      });
    } catch (e) {
      console.log('translations could not be loaded. TODO: translations for test');
    }

    function findBestGuess(res, translation) {
      translation = translation.toLowerCase();
      for (let value of res) {
        if (value.key.toLowerCase() === translation) {
          return value;
        }
      }

      for (let value of res) {
        if (value.key.toLowerCase().indexOf(translation) > -1) {
          return value;
        }
      }

      return res[0];
    }

    // This is to help find translation keys
    window['findKey'] = function (translation: string) {
      console.groupCollapsed('Translation key lookup');
      let all = Translation.all;
      let res = [];
      for (let key in all) {
        if (all[key] === translation) {
          res.push({ key: key, translation: all[key] });
        }
      }

      res = res.sort((a, b) => {
        if (a.key.length < b.key.length) {
          return -1;
        } else {
          return 1;
        }
      });

      let groupName = 'Exact match';
      if (res.length < 1) {
        console.log('None found for:', `'${translation}'`, 'switching to loose search.');
        res = window['findKeyLoose'](translation);
        groupName = 'Best guess';
      }

      console.log(res);
      console.groupEnd();

      if (res.length > 0) {
        console.group(groupName);
        let guess = findBestGuess(res, translation);
        console.log('%ckey:', 'color: blue;', guess.key);
        //console.log('%ccurrent:', 'color: blue;', guess.translation);
        console.log('%cen_GB:', 'color: blue;', en[guess.key]);
        console.log('%cfi_FI:', 'color: blue;', fi[guess.key]);
        console.groupEnd();
      } else {
        console.log('%cNo translations found', 'color: red');
      }
    };

    window['findKeyLoose'] = function (translation: string) {
      translation = translation.toLowerCase();
      let a = translation.split(' ');
      let res = [];

      let all = Translation.all;
      for (let key in all) {
        let tr = all[key].toLowerCase();
        let valid = true;
        for (let value of a) {
          if (tr.indexOf(value) < 0) {
            valid = false;
            break;
          }
        }

        if (valid) {
          res.push({ key: key, translation: all[key] });
        }
      }

      res = res.sort((a, b) => {
        if (a.translation.length === b.translation.length) {
          if (a.key.length < b.key.length) {
            return -1;
          } else {
            return 1;
          }
        } else {
          if (a.translation.length < b.translation.length) {
            return -1;
          } else {
            return 1;
          }
        }
      });
      return res;
    };
  }
  */
}

export function l(key: string, prefix?: string) {
  return Translation.l(key, prefix);
}

export function tsxl(key: string, prefix?: string): JSX.Element {
  return Translation.tsxl(key, prefix);
}

/* Multiline translation */
export function ml(key: string) {
  return (l(key) || '').replace(/<br>/g, '\n');
}

/* Returns a list of translations with the same key + index
 *
 * example:
 * translation0: 'foo',
 * translation1: 'bar'
 * tlist('translation') returns ['foo', 'bar']
 *
 * Loop stops when no translation for index is found and l(key) returns [key]
 * Max list size of 10 is just a precaution.
 */
export function tlist(key: string): string[] {
  let index = 0;
  const maxListSize = 10;
  const list = [];
  while (l(key + index) !== `[${key + index}]` && index < maxListSize) {
    list.push(l(key + index));
    index++;
  }
  if (list.length === 0) {
    list.push(`[${key}]`);
  }
  return list;
}
/*
window['dev'] = Translation.dev;
if (window.location.host.indexOf('local') > -1) {
  Translation.dev();
}
*/
